import React, { useEffect, useState } from 'react';
import { Box, DialogContent, DialogActions, Button } from '@mui/material';
import { useFormik, FormikProvider } from 'formik';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../common/dialog/BootstrapDialog';
import * as yup from 'yup';
import DetailTemplate from '../../common/template/DetailTemplate';
import ExportForm from './ExportForm';
import _ from 'lodash';
import StockListContainer from '../../../containers/inventorymng/products/StockListContainer';
import { whUuid } from '../../../lib/api/inventory';
import * as invApi from '../../../lib/api/inventory';

import { listInventoryStocks } from '../../../modules/inventoryModule/invStocks';

const ProductExportModal = ({ open, handleClose, data }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [totalPrice, setTotalPrice] = useState(0);

  //체크된 상품들
  const onSelectedProducts = (products) => {
    const checkedProducts = products.map((product) => ({
      stockUuid: product.stockUuid,
      whUuid: product.whUuid,
      productUuid: product.productUuid,
      partnerNm: product.masPartnerNm,
      pricePartnerNm: product.pricePartnerNm,
      category: product.category,
      priceUuid: product.priceUuid,
      productNm: product.productNm,
      modelNm: product.modelNm,
      trnUuid: product.trnUuid,
      productCnt: 1,
      priceVal: product.priceVal,
    }));

    //차집합
    const diff = checkedProducts.filter(
      (p) =>
        _.findIndex(formik.values.prdtList, { priceUuid: p.priceUuid }) === -1,
    );
    //console.log(diff, 'd');
    if (diff.length > 0) {
      formik.setFieldValue('prdtList', [...formik.values.prdtList, ...diff]);
    }
  };

  const [initialValues, setInitialValues] = useState({
    prjUuid: '',
    outDesc: '', //출고사유
    prdtList: [],
  });

  useEffect(() => {
    if (data) {
      setInitialValues(data);
    }
  }, [data]);

  const validationSchema = yup.object({
    prjUuid: yup.string('').required('프로젝트를 선택하세요'),
    outDesc: yup.string('').required('출고사유를 입력하세요'),
    prdtList: yup.array().of(
      yup.object().shape({
        priceUuid: yup.string().nullable(),
        productCnt: yup
          .number('수량은 숫자만 가능합니다')
          .min(1, '제품 수량을 확인하세요')
          .when('priceUuid', (priceUuid, schema) => {
            if (priceUuid) return schema.required('제품수량을 입력하세요');
          }),
        priceVal: yup
          .number('단가는 숫자만 가능합니다')
          .min(0, '제품 단가를 확인하세요')
          .when('priceUuid', (priceUuid, schema) => {
            if (priceUuid) return schema.required('제품 단가를 입력하세요');
          }),
      }),
    ),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      // console.log(JSON.stringify(values, null, 2));
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (formik && formik.values.prdtList) {
      let total = 0;
      _.forEach(formik.values?.prdtList, (p) => {
        total += parseInt(p.priceVal) * parseInt(p.productCnt);
      });

      setTotalPrice(total);
    }
  }, [formik.values?.prdtList]);

  const onSave = async (values) => {
    const dataToSubmit = {
      productList: values.prdtList.map((prd) => ({
        ...prd,
        whUuid: whUuid,
      })),
      transInf: {
        trnNm: makeTitle(values.prdtList),
        trnDesc: values.outDesc,
        trnTyp: 'O',
        trnFromWh: whUuid,
        trnToWh: values.prjUuid,
      },
    };
    //console.log(JSON.stringify(dataToSubmit, null, 2));

    try {
      const res = await invApi.addStockInfo(dataToSubmit);
      if (res.data) {
        enqueueSnackbar('출고 되었습니다', {
          variant: 'success',
        });
        dispatch(listInventoryStocks());
        handleClose();
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const makeTitle = (prdtList) => {
    let title =
      prdtList.length > 1
        ? `${prdtList[0].productNm} 외 ${prdtList.length - 1} 건 제품 출고`
        : `${prdtList[0].productNm} 출고`;
    return title;
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="dialog-title"
      open={open}
      fullWidth={true}
      maxWidth="lg"
    >
      <BootstrapDialogTitle id="dialog-title" onClose={handleClose}>
        출고 관리
      </BootstrapDialogTitle>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <DialogContent
          dividers
          sx={{
            height: 600,
            overflowY: 'auto',
          }}
        >
          <Box>
            <StockListContainer onSelectedProducts={onSelectedProducts} />
          </Box>

          <DetailTemplate title="출고 내역">
            <FormikProvider value={formik}>
              <ExportForm formik={formik} totalPrice={totalPrice} />
            </FormikProvider>
          </DetailTemplate>
        </DialogContent>
        <DialogActions>
          <Box>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={formik.isSubmitting}
            >
              출고
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </BootstrapDialog>
  );
};

export default ProductExportModal;
